import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import CheckboxTsoPq from '@components/Checkbox/CheckboxTsoPq';
import { StyledTreeItem } from '@components/ListItem/styledTreeItem';
import type { TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { faCheck, faX } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Autocomplete,
  Box,
  FormControlLabel,
  ListItemIcon,
  Paper,
  Popper,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view';
import type { RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

type TreeAutocompleteTsoPqProps = {
  options: TreeListItemTsoPqProps[];
  supportingText?: string;
  error?: boolean;
  customwidth?: string;
  mandatory?: boolean;
  label: string;
  multipleValues?: boolean;
  disabled?: boolean;
  defaultValue?: string[];
  placeholder?: string;
  register?: unknown;
  watch?: unknown;
  name?: string;
  setValue?: (name: string | undefined, value: string[]) => void;
  limitTags?: number;
  multiple?: boolean;
  handleChange?: (selected: string[], checked?: string[], isUserChange?: boolean) => void;
  syncSelectedIdsToForm?: boolean;
  lowestLevel?: number;
  collapseSignal?: number;
};

const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => !['width'].includes(prop as string),
})(({ width }: { width: string | undefined }) => {
  return {
    width: width,
  };
});

const StyledInput = styled(TextField, {
  shouldForwardProp: (prop) => !['width'].includes(prop as string),
})(({ width, error }: { width: string | undefined; error: boolean }) => {
  return {
    background: tsoPqTheme.palette.background.other.color_other_background_white,
    width: width,
    flex: 1,
    '& .MuiInputBase-root': {
      minHeight: '2.125rem',
      width: width ?? '13.25rem',
      gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
      fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
      fontFamily: tsoPqTheme.typography.fontFamily,
      borderColor: error && tsoPqTheme.palette.customError.color_signal_error_error,
      padding: '0.25rem 0 0.25rem 0.5rem',

      '&:hover': {
        background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled_lt,
      },

      '&.Mui-focused': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_lt,
      },

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_hover,
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_hover}`,
      },

      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.customError.color_signal_error_error}`,
      },

      '&.Mui-disabled': {
        backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_disabled,

        '& .MuiInputBase-input': {
          minHeight: '28px',
          WebkitTextFillColor: tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled,
        },
      },

      '& .MuiAutocomplete-input': {
        padding: '0rem !important',
      },
    },
  };
});

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => !['width'].includes(prop as string),
})(({ width }: { width: string | undefined }) => {
  return {
    width: width,
    height: '16.75rem',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flexStart',
    alignSelf: 'stretch',
    '&.MuiPaper-root': {
      padding: '0rem',
    },
  };
});

const StyledAutocompleteChip = styled(Box)(() => {
  return {
    height: '1.125rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
    padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8}`,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,

    '&.MuiAutocomplete-tag': {
      margin: '0',
    },
  };
});

const StyledLabel = styled(Typography)(() => {
  return {
    display: 'flex',
    color: tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '1rem',
    padding: '0 0 0 0.25rem',
  };
});

const StyledSelectContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    width: 'fit-content',
  };
});

const StyledMandatory = styled('span')(() => {
  return {
    color: tsoPqTheme.palette.customError.color_signal_error_error,
    fontFamiliy: tsoPqTheme.typography.fontFamily,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '0.875rem',
  };
});

const StyledSupportingText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'error',
})(({ error }: { error?: boolean }) => {
  return {
    color: error
      ? tsoPqTheme.palette.customError.color_signal_error_error
      : tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontFamily: tsoPqTheme.typography.fontFamily,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '0.875rem',
    padding: '0 0 0 0.5rem',
  };
});

const StyledIconButtonTsoPq = styled(IconButtonTsoPq)(() => {
  return {
    minHeight: '0.5rem',
    minWidth: '0.5rem',
    padding: '0rem',
  };
});

const StyledListItemIcon = styled(ListItemIcon)(() => {
  return {
    width: '2px',
    '& svg': {
      width: '0.875rem',
      color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent,
      margin: 'auto',
    },
  };
});

const flattenTree = (nodes: TreeListItemTsoPqProps[], map: Record<string, string> = {}): Record<string, string> => {
  for (const node of nodes) {
    map[node.id] = node.label;
    if (node.options) flattenTree(node.options, map);
  }
  return map;
};

const isLeaf = (node: TreeListItemTsoPqProps): boolean => !node.options || node.options.length === 0;

const arraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const getAllDescendants = (node: TreeListItemTsoPqProps): string[] => {
  let ids = [node.id];
  if (node.options) {
    for (const child of node.options) {
      ids = ids.concat(getAllDescendants(child));
    }
  }
  return ids;
};

export const findNodeById = (id: string, nodes: TreeListItemTsoPqProps[]): TreeListItemTsoPqProps | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.options) {
      const found = findNodeById(id, node.options);
      if (found) return found;
    }
  }
  return null;
};

const findNodeLevel = (id: string, nodes: TreeListItemTsoPqProps[], level = 1): number | null => {
  for (const node of nodes) {
    if (node.id === id) return level;
    if (node.options) {
      const childLevel = findNodeLevel(id, node.options, level + 1);
      if (childLevel) return childLevel;
    }
  }
  return null;
};

const findDeepestLeaves = (node: TreeListItemTsoPqProps): string[] => {
  if (!node.options || node.options.length === 0) return [node.id];
  return node.options.flatMap(findDeepestLeaves);
};

const findLeavesAtLevel = (
  startNode: TreeListItemTsoPqProps,
  targetLevel: number,
  treeRoot: TreeListItemTsoPqProps[]
): string[] => {
  const result: string[] = [];

  const traverse = (node: TreeListItemTsoPqProps) => {
    const level = findNodeLevel(node.id, treeRoot) ?? 0;
    const leafNode = !node.options || node.options.length === 0;

    if (leafNode) {
      if (level === targetLevel) {
        result.push(node.id);
      }
      return;
    }

    for (const childNode of node.options) {
      traverse(childNode);
    }
  };

  traverse(startNode);
  return result;
};

const getAncestorIds = (
  nodes: TreeListItemTsoPqProps[],
  nodeId: string,
  ancestors: string[] = []
): string[] | null => {
  for (const node of nodes) {
    if (node.id === nodeId) return ancestors;
    if (node.options) {
      const found = getAncestorIds(node.options, nodeId, [...ancestors, node.id]);
      if (found) return found;
    }
  }
  return null;
};

const normalizeToAllowedLeaves = (
  ids: string[],
  options: TreeListItemTsoPqProps[],
  lowestLevel?: number
): string[] => {
  if (!ids.length) return [];

  const normalizedIds = ids.flatMap((id) => {
    const node = findNodeById(id, options);
    if (!node) return [];
    return typeof lowestLevel === 'number' ? findLeavesAtLevel(node, lowestLevel, options) : findDeepestLeaves(node);
  });

  return [...new Set(normalizedIds)];
};

const RecursiveTreeItem = ({
  node,
  checked,
  toggleNode,
  multiple = true,
}: {
  node: TreeListItemTsoPqProps;
  checked: string[];
  toggleNode: (node: TreeListItemTsoPqProps) => void;
  multiple: boolean;
}) => {
  const descendants = getAllDescendants(node);
  const isChecked = descendants.every((id) => checked.includes(id));
  const isIndeterminate = !isChecked && descendants.some((id) => checked.includes(id));
  const handleNodeSelection = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleNode(node);
  };

  return (
    <StyledTreeItem
      leftIcon
      itemId={node.id}
      label={
        multiple ? (
          <FormControlLabel
            control={
              <CheckboxTsoPq
                checked={isChecked}
                indeterminate={isIndeterminate}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleNode(node);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            }
            label={node.label}
            onClick={handleNodeSelection}
          />
        ) : (
          <FormControlLabel
            control={
              <StyledListItemIcon selected={checked.at(0) === node.id}>
                {checked.at(0) === node.id && <FontAwesomeIcon icon={faCheck} />}
              </StyledListItemIcon>
            }
            label={node.label}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isLeaf(node)) toggleNode(node);
            }}
          />
        )
      }
    >
      {node.options?.map((option) => (
        <RecursiveTreeItem
          key={option.id}
          node={option}
          toggleNode={toggleNode}
          checked={checked}
          multiple={multiple}
        />
      ))}
    </StyledTreeItem>
  );
};

let paperScrollPosition = 0;

const CustomPaper = ({
  customwidth,
  paperRef,
  expandedNodes,
  options,
  checked,
  toggleNode,
  handleExpandedItemsChange,
  multiple = true,
}: {
  customwidth: string | undefined;
  paperRef: RefObject<HTMLDivElement | null>;
  expandedNodes: string[];
  options: TreeListItemTsoPqProps[];
  checked: string[];
  toggleNode: (node: TreeListItemTsoPqProps) => void;
  handleExpandedItemsChange: (event: React.SyntheticEvent, itemIds: string[]) => void;
  multiple: boolean;
}) => {
  const handleScroll = () => {
    if (paperRef.current) {
      paperScrollPosition = paperRef.current.scrollTop;
    }
  };

  useEffect(() => {
    if (paperRef.current) {
      paperRef.current.scrollTop = paperScrollPosition;
    }
  });

  return (
    <StyledPaper
      width={customwidth}
      ref={paperRef}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onScroll={handleScroll}
    >
      <SimpleTreeView
        expandedItems={expandedNodes}
        expansionTrigger="iconContainer"
        onExpandedItemsChange={handleExpandedItemsChange}
      >
        {options.map((node) => (
          <RecursiveTreeItem key={node.id} node={node} checked={checked} toggleNode={toggleNode} multiple={multiple} />
        ))}
      </SimpleTreeView>
    </StyledPaper>
  );
};

const TreeAutocompleteTsoPq = ({
  options,
  supportingText,
  error = false,
  customwidth,
  mandatory,
  label,
  multiple = true,
  disabled = false,
  defaultValue,
  placeholder,
  limitTags = 2,
  lowestLevel,
  collapseSignal,
  handleChange,
  syncSelectedIdsToForm = true,
  watch: _watch,
  name,
  setValue,
}: TreeAutocompleteTsoPqProps) => {
  const [checkedNodeIds, setCheckedNodeIds] = useState<string[]>([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const isUserChangeRef = useRef(false);
  const manualExpandedNodeIdsRef = useRef<string[]>([]);

  const popperContainerRef = useRef<HTMLDivElement | null>(null);
  const autocompleteContainerRef = useRef<HTMLDivElement>(null);

  const labelLookup = useMemo(() => flattenTree(options), [options]);
  const normalizedDefaultValue = useMemo(
    () => normalizeToAllowedLeaves(defaultValue ?? [], options, lowestLevel),
    [defaultValue, lowestLevel, options]
  );
  const selectedSingleNodeId = selectedNodeIds[0] ?? null;
  const autocompleteValue = multiple ? selectedNodeIds : selectedSingleNodeId;
  let autocompleteInputValue = searchText;

  if (!multiple && !searchText && !isOpen && selectedSingleNodeId) {
    autocompleteInputValue = labelLookup[selectedSingleNodeId] ?? '';
  }
  const validLeafNodeIds = useMemo(
    () =>
      Object.keys(labelLookup).filter((id) => {
        const node = findNodeById(id, options);
        if (!node || !isLeaf(node)) return false;
        if (typeof lowestLevel === 'number') {
          const level = findNodeLevel(id, options);
          return level === lowestLevel;
        }
        return true;
      }),
    [labelLookup, lowestLevel, options]
  );

  const toggleSelection = (node: TreeListItemTsoPqProps) => {
    const leafIds =
      typeof lowestLevel === 'number' ? findLeavesAtLevel(node, lowestLevel, options) : findDeepestLeaves(node);

    if (leafIds.length === 0) return;
    isUserChangeRef.current = true;

    const alreadySelected = leafIds.every((id) => checkedNodeIds.includes(id));

    if (multiple) {
      if (alreadySelected) {
        setCheckedNodeIds((previous) => previous.filter((id) => !leafIds.includes(id)));
        setSelectedNodeIds((previous) => previous.filter((id) => !leafIds.includes(id)));
      } else {
        setCheckedNodeIds((previous) => [...new Set([...previous, ...leafIds])]);
        setSelectedNodeIds((previous) => [...new Set([...previous, ...leafIds])]);
      }
    } else {
      const targetLeaf = leafIds[0];
      if (!targetLeaf) return;
      if (alreadySelected) {
        setCheckedNodeIds([]);
        setSelectedNodeIds([]);
        setSearchText('');
      } else {
        setCheckedNodeIds([targetLeaf]);
        setSelectedNodeIds([targetLeaf]);
        setSearchText('');
        setIsOpen(false);
      }
    }
  };

  useEffect(() => {
    if (syncSelectedIdsToForm) {
      setValue?.(name, selectedNodeIds);
    }
    if (isUserChangeRef.current) {
      handleChange?.(selectedNodeIds, checkedNodeIds, true);
    }
    isUserChangeRef.current = false;
  }, [checkedNodeIds, handleChange, name, selectedNodeIds, setValue, syncSelectedIdsToForm]);

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      const searchTerms = searchText.replaceAll(' ', '').split(',');
      for (const term of searchTerms) {
        const matchedLabel = Object.values(labelLookup).find((label) => label.toLowerCase() === term.toLowerCase());
        const matchedId = Object.entries(labelLookup).find(([_, label]) => label === matchedLabel)?.[0] ?? '';
        if (matchedLabel && !selectedNodeIds.includes(matchedId)) {
          toggleSelection({ id: matchedId, label: matchedLabel } as TreeListItemTsoPqProps);
        }
      }
    }

    if (event.key === 'Backspace' && selectedNodeIds.length > 0 && searchText === '') {
      const lastSelectedId = selectedNodeIds.at(-1);
      if (lastSelectedId) {
        toggleSelection({ id: lastSelectedId, label: labelLookup[lastSelectedId] ?? '' } as TreeListItemTsoPqProps);
      }
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (!popperContainerRef.current?.contains(target) && !autocompleteContainerRef.current?.contains(target)) {
      setIsOpen(false);
    }
  };

  const handleExpandChange = (_: React.SyntheticEvent, ids: string[]) => {
    if (searchText.length === 0) {
      manualExpandedNodeIdsRef.current = ids;
    }
    setExpandedNodeIds(ids);
  };

  const handleTagRemove = (id: string) => {
    toggleSelection({ id, label: labelLookup[id] } as TreeListItemTsoPqProps);
  };

  useEffect(() => {
    const nextExpanded =
      searchText.length === 0
        ? manualExpandedNodeIdsRef.current
        : Object.keys(labelLookup).reduce((accumulator, nodeId: string) => {
          if (!labelLookup[nodeId].toLowerCase().includes(searchText.toLowerCase())) {
            return accumulator;
          }

          const ancestors = getAncestorIds(options, nodeId) || [];
          return [...new Set([...accumulator, ...ancestors])];
        }, [] as string[]);

    setExpandedNodeIds((previous) => (arraysEqual(previous, nextExpanded) ? previous : nextExpanded));
  }, [labelLookup, options, searchText]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const nextSelected = selectedNodeIds.filter((id) => validLeafNodeIds.includes(id));
    const nextChecked = checkedNodeIds.filter((id) => validLeafNodeIds.includes(id));

    if (!arraysEqual(nextSelected, selectedNodeIds)) {
      setSelectedNodeIds(nextSelected);
    }

    if (!arraysEqual(nextChecked, checkedNodeIds)) {
      setCheckedNodeIds(nextChecked);
    }
  }, [checkedNodeIds, selectedNodeIds, validLeafNodeIds]);

  useEffect(() => {
    if (!defaultValue) return;
    const shouldSyncSelection = !arraysEqual(normalizedDefaultValue, selectedNodeIds);
    const shouldSyncChecked = !arraysEqual(normalizedDefaultValue, checkedNodeIds);

    if (shouldSyncSelection) {
      setSelectedNodeIds(normalizedDefaultValue);
    }

    if (shouldSyncChecked) {
      setCheckedNodeIds(normalizedDefaultValue);
    }
  }, [checkedNodeIds, defaultValue, normalizedDefaultValue, selectedNodeIds]);

  useEffect(() => {
    manualExpandedNodeIdsRef.current = [];
    setSearchText('');
    setExpandedNodeIds((previous) => (previous.length === 0 ? previous : []));
  }, [collapseSignal]);

  return (
    <StyledSelectContainer>
      <StyledLabel>
        {label}
        {mandatory && <StyledMandatory>*</StyledMandatory>}
      </StyledLabel>
      <StyledAutocomplete
        disabled={disabled}
        ref={autocompleteContainerRef}
        width={customwidth}
        multiple={multiple}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={(event, _) => {
          const target = event?.target as Node | null;
          if (target && (popperContainerRef.current?.contains(target) || autocompleteContainerRef.current?.contains(target))) {
            return;
          }
          setIsOpen(false);
        }}
        disableCloseOnSelect
        disablePortal
        limitTags={limitTags}
        options={validLeafNodeIds}
        value={autocompleteValue}
        inputValue={autocompleteInputValue}
        isOptionEqualToValue={(option, value) => option === value}
        onInputChange={(_, val, reason) => {
          if (reason === 'input' || reason === 'clear') {
            setSearchText(val);
          }
        }}
        getOptionLabel={(id) => labelLookup[id] || id}
        renderInput={(params) => (
          <StyledInput
            width={customwidth}
            error={error}
            onClick={() => setIsOpen(true)}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            {...params}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.map((id, index) => (
            <StyledAutocompleteChip {...getTagProps({ index })}>
              {labelLookup[id]}
              <StyledIconButtonTsoPq
                onClick={() => handleTagRemove(id)}
                icon={faX}
                buttonType="transparentNeutralNoEffect"
                sizeVariant="s"
                disabled={disabled}
              />
            </StyledAutocompleteChip>
          ))
        }
        slots={{
          popper: (props) =>
            !disabled && (
              <Popper
                {...props}
                placement="bottom-start"
                style={{ width: autocompleteContainerRef.current?.offsetWidth, zIndex: 1300 }}
              />
            ),
          paper: (_) => (
            <CustomPaper
              customwidth={customwidth}
              paperRef={popperContainerRef}
              expandedNodes={expandedNodeIds}
              options={options}
              checked={checkedNodeIds}
              toggleNode={toggleSelection}
              handleExpandedItemsChange={handleExpandChange}
              multiple={multiple}
            />
          ),
        }}
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
          },
          popupIndicator: {
            onClick: (e) => {
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            },
          },
          clearIndicator: {
            onClick: (e) => {
              e.stopPropagation();
              isUserChangeRef.current = true;
              setSelectedNodeIds([]);
              setCheckedNodeIds([]);
            },
          },
        }}
        popupIcon={<KeyboardArrowDownIcon />}
      />
      {supportingText && <StyledSupportingText error={error}>{supportingText}</StyledSupportingText>}
    </StyledSelectContainer>
  );
};

export default TreeAutocompleteTsoPq;
